import React, {useState, useEffect, useMemo, useCallback} from "react";
import {Link} from "react-router-dom";
import {dateFormatter, getImageFromFilesbyKey} from "../../../../_metronic/_helpers";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

//actions:
import {mediaItemsListActions} from "../../../redux/MediaItemsList/mediaItemsListRedux";
import {
    deleteMediaItem,
    editMediaItem,
    uploadMediaItemImage,
    deleteUploadedImage,
} from "../../../redux/MediaItemsList/mediaItemsListCrud";

//components:
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
// import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@material-ui/icons/PlayCircleFilledWhiteOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import {CustomSwitch, CustomTooltip} from "../../../widgets";
import {makeStyles} from "@material-ui/core/styles";

const videoListStyles = makeStyles(theme => ({
    listbox: {
        maxHeight: "180px !important",
        overflow: "hidden scroll !important",
        minWidth: "85px",
        width: "fit-content",
    },
}));

const useTableDetails = () => {
    const {t} = useTranslation();
    const classes = videoListStyles();

    const headRows = [
        {
            id: "type_id",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.TYPE"),
        },
        {
            id: "title",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.TITLE"),
        },
        {
            id: "track_id",
            align: "left",
            disablePadding: false,
            label: t("GENERAL.TRACK"),
        },
        {
            id: "created_at",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.UPLOADED_AT"),
        },
        {
            id: "updated_at",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.LAST_UPDATE"),
        },
        {
            id: "actions",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.ACTIONS"),
        },
    ];

    //redux:
    //store:
    const {allMediaItems = []} = useSelector(state => state.mediaItemsList);
    const normalTracks = useSelector(state => state.tracks.normalTracks);
    //actions:
    const dispatch = useDispatch();
    const getAllMediaItems = () => dispatch(mediaItemsListActions.getAllMediaItems());
    const setLoader = bool => dispatch(mediaItemsListActions.setLoader(bool));

    //local state:
    const [filteredData, setFilteredData] = useState(allMediaItems);
    const [videoId, setVideoId] = useState("");
    const [videoDetailsForThumbnailModal, setVideoDetailsForThumbnailModal] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditTitleInput, setShowEditTitleInput] = useState("");
    const [showEditTrackInput, setShowEditTrackInput] = useState("");
    const [openUploadImageModal, setOpenUploadImageModal] = useState(false);
    const [showDeletedModal, setShowDeletedModal] = useState(false);
    const [showDeletedFailedModal, setShowDeletedFailedModal] = useState(false);

    useEffect(() => {
        if (allMediaItems && allMediaItems.length) {
            if (openUploadImageModal === true) {
                const video = allMediaItems.find(e => e.id === videoId);
                setVideoDetailsForThumbnailModal(video);
            }
        }
    }, [allMediaItems]);

    const onVideoChangeHandler = useCallback(({id, title, track_id, visible = null}) => {
        if (id) {
            if ((title && !track_id) || (track_id && !title) || (!track_id && !title && visible !== null)) {
                if (title) {
                    editMediaItem(id, {title}).then(() => getAllMediaItems());
                } else if (track_id) {
                    editMediaItem(id, {track_id}).then(() => getAllMediaItems());
                } else if (visible !== null) {
                    editMediaItem(id, {visible}).then(() => getAllMediaItems());
                }
            }
        }
    }, []);

    const onDeleteVideoHandler = useCallback(() => {
        deleteMediaItem({
            videoId,
            onSuccess: () => {
                setShowDeletedModal(true);
                setLoader(false);
                getAllMediaItems();
            },
            onError: () => {
                setShowDeletedFailedModal(true);
                setLoader(false);
            },
        });

        setShowDeleteModal(false);
        setLoader(true);
        setVideoId("");
    }, [videoId]);

    //functions for IMAGE EDITOR MODAL:
    const onDeleteImage = useCallback(({videoId, fileId, onConfirmDeleted, onErrorCall}) => {
        const apiCalls = async () => {
            try {
                const deleted = await deleteUploadedImage(videoId, fileId);
                if (deleted.data.result === "success") {
                    getAllMediaItems();
                    onConfirmDeleted();
                }
            } catch (err) {
                console.error(err);
                onErrorCall();
            }
        };

        apiCalls();
    }, []);

    const uploadVideoImg = useCallback(({id, file, onConfirmUploaded, onError}) => {
        if (file) {
            const apiCalls = async () => {
                try {
                    const onSuccess = () => {
                        getAllMediaItems();
                        onConfirmUploaded();
                    };

                    uploadMediaItemImage({id, file, onSuccess, onError});
                } catch (err) {
                    console.error(err);
                }
            };
            apiCalls();
        }
    }, []);

    const formatRow = useCallback(
        (
            {
                id = 0,
                files = [],
                title = "",
                track = "",
                track_id = 0,
                created_at = "",
                updated_at = "",
                visible = 0,
                status = {id: 2, value: "Completed", type: "light"},
                type_id,
                thumbnail = getImageFromFilesbyKey(files, id, "imgSquare", type_id),
            },
            i
        ) => {
            return (
                <TableRow key={i}>
                    <TableCell align="left" className="text-muted mb-0">
                        {type_id === 1 ? (
                            <PlayCircleFilledWhiteOutlinedIcon className="w-30px h-30px" />
                        ) : (
                            <DescriptionOutlinedIcon className="w-30px h-30px" />
                        )}
                    </TableCell>
                    <TableCell align="left" component="th" scope="row" style={{minWidth: 200, maxWidth: 400}}>
                        <div className="d-flex align-items-center">
                            <div className="d-flex position-relative">
                                <span
                                    className="bg_image_cover rounded w-50px h-50px"
                                    style={{
                                        backgroundImage: `url(${thumbnail})`,
                                    }}
                                />
                                <CustomTooltip
                                    config={{
                                        text: t("TOOLTIP.EDIT.IMAGE"),
                                        placement: "top",
                                    }}
                                >
                                    <button
                                        onClick={() => {
                                            setOpenUploadImageModal(true);
                                            setVideoId(id);
                                            const thisVideo = filteredData.find(e => e.id === id);
                                            setVideoDetailsForThumbnailModal(thisVideo);
                                        }}
                                        className={`btn btn-icon btn-light position-absolute right-0 bottom-0 edit_button ${showEditTitleInput ||
                                            (showEditTrackInput && "d-none")}`}
                                    >
                                        <EditIcon />
                                    </button>
                                </CustomTooltip>
                            </div>

                            <div className="ml-4 w-100">
                                {showEditTitleInput !== id && (
                                    <div
                                        className={`${
                                            showEditTitleInput === id ? "d-none" : "d-block"
                                        } text-dark-75 font-size-base mb-0`}
                                    >
                                        {title}
                                        <CustomTooltip
                                            config={{
                                                text: t("TOOLTIP.EDIT.TITLE"),
                                                placement: "top",
                                            }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setShowEditTitleInput(id);
                                                }}
                                                className={`btn btn-icon btn-light ml-2 edit_button ${showEditTitleInput ||
                                                    (showEditTrackInput && "d-none")}`}
                                            >
                                                <EditIcon />
                                            </button>
                                        </CustomTooltip>
                                    </div>
                                )}
                                {showEditTitleInput === id && (
                                    <input
                                        type="text"
                                        autoFocus
                                        className="form-control form-control-solid h-auto py-2 px-4 text-dark-75 font-size-base w-100"
                                        defaultValue={title}
                                        onKeyDown={e => {
                                            const value = e.target.value.replace(/\s\s+/g, " ");
                                            if (e.key === "Enter") {
                                                const trimmedTitle = value.trim();
                                                if (trimmedTitle && trimmedTitle !== title) {
                                                    onVideoChangeHandler({
                                                        id,
                                                        title: trimmedTitle,
                                                    });
                                                }
                                                setShowEditTitleInput("");
                                                e.target.value = trimmedTitle;
                                            } else e.target.value = value;
                                        }}
                                        onBlur={e => {
                                            const value = e.target.value.trim();
                                            if (value) {
                                                value !== title &&
                                                    onVideoChangeHandler({
                                                        id,
                                                        title: value,
                                                    });
                                            }
                                            setShowEditTitleInput("");
                                            e.target.value = value;
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </TableCell>

                    <TableCell align="left" className="mb-0 font-size-1rem">
                        {showEditTrackInput !== id ? (
                            <div className="d-flex">
                                <div className="py-5 w-fit-content w-100px">
                                    {track?.name}

                                    <CustomTooltip
                                        config={{
                                            text: t("TOOLTIP.EDIT.TRACK"),
                                            placement: "top",
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                setShowEditTrackInput(id);
                                            }}
                                            className={`btn btn-icon btn-light ml-2 edit_button ${showEditTitleInput ||
                                                (showEditTrackInput && "d-none")}`}
                                        >
                                            <EditIcon />
                                        </button>
                                    </CustomTooltip>
                                </div>
                            </div>
                        ) : (
                            <div className="videos_track_dropdown">
                                <Autocomplete
                                    open={showEditTrackInput === id}
                                    popupIcon={null}
                                    options={normalTracks || []}
                                    className={classes.listbox}
                                    blurOnSelect
                                    size="small"
                                    fullWidth
                                    closeIcon={false}
                                    defaultValue={normalTracks.find(ed => ed.track_id === track_id)}
                                    getOptionLabel={option => (option && option.name) || ""}
                                    onChange={(e, value) => {
                                        if (value && value.track_id !== track_id)
                                            onVideoChangeHandler({
                                                id: id,
                                                track_id: value.track_id,
                                            });
                                        setShowEditTrackInput("");
                                    }}
                                    renderInput={(params, i) => (
                                        <TextField
                                            {...params}
                                            key={i}
                                            autoFocus
                                            className="py-2 overflow-hidden d-flex justify-content-start align-items-center"
                                            variant="outlined"
                                            onKeyDown={e => {
                                                if (e.key === "Enter") setShowEditTrackInput("");
                                            }}
                                            onBlur={() => setShowEditTrackInput("")}
                                            label=""
                                            type="text"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </TableCell>
                    <TableCell align="left" className="text-primary mb-0 table_formatted_date">
                        {created_at && dateFormatter(created_at)}
                    </TableCell>
                    <TableCell align="left" className="text-primary mb-0 table_formatted_date">
                        {updated_at && dateFormatter(updated_at)}
                    </TableCell>

                    {/* actions: */}
                    <TableCell align="left">
                        {status && status.id === 2 ? (
                            <div className="actions">
                                <CustomTooltip
                                    config={{
                                        text: t("TOOLTIP.PLAY_VIDEO"),
                                        placement: "top",
                                    }}
                                >
                                    <Link className="mr-3" to={`/media/${id}`}>
                                        <button className="btn btn-icon btn-light btn-hover-primary btn-sm button_blue_icon">
                                            <VisibilityIcon />
                                        </button>
                                    </Link>
                                </CustomTooltip>
                                <CustomTooltip
                                    config={{
                                        text: t("TOOLTIP.REVIEW_VIDEO"),
                                        placement: "top",
                                    }}
                                >
                                    <Link className="mr-3" to={`/review/${id}`}>
                                        <button className="btn btn-icon btn-light btn-hover-primary btn-sm button_blue_icon">
                                            <EditIcon />
                                        </button>
                                    </Link>
                                </CustomTooltip>
                                <CustomTooltip
                                    config={{
                                        text: t("TOOLTIP.DELETE.VIDEO"),
                                        placement: "top",
                                    }}
                                >
                                    <button
                                        className="btn btn-icon btn-light btn-hover-primary btn-sm mr-3 button_blue_icon"
                                        onClick={() => {
                                            setVideoId(id);
                                            setShowDeleteModal(true);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </button>
                                </CustomTooltip>

                                <CustomTooltip
                                    config={{
                                        text: t("TOOLTIP.SHOW_HIDE"),
                                        placement: "top",
                                    }}
                                >
                                    <div className="d-flex">
                                        <CustomSwitch
                                            size="small"
                                            checked={!!visible}
                                            onChange={e => {
                                                onVideoChangeHandler({
                                                    id,
                                                    visible: e.target.checked ? 1 : 0,
                                                });
                                            }}
                                        />
                                    </div>
                                </CustomTooltip>
                            </div>
                        ) : (
                            <Button className={`btn btn-${(status && status.type) || "light"} w-100`} disabled>
                                {(status && status.value) || ""}
                            </Button>
                        )}
                    </TableCell>
                </TableRow>
            );
        },
        [filteredData, showEditTitleInput, showEditTrackInput]
    );

    const emptyMessage = useMemo(
        () => (
            <>
                {t("ADMINPANEL.VIDEOS.TABLE.EMPTY_MESSAGE")}
                <Link to={"/adminpanel/upload"}>{t("ADMINPANEL.UPLOAD.TITLE")}</Link>
            </>
        ),
        [t]
    );

    return {
        allMediaItems,
        headRows,
        formatRow,
        emptyMessage,
        videoDetailsForThumbnailModal,
        showDeleteModal,
        showDeletedModal,
        showDeletedFailedModal,
        onDeleteVideoHandler,
        onDeleteImage,
        uploadVideoImg,
        filteredData,
        setFilteredData,
        openUploadImageModal,
        setOpenUploadImageModal,
        setShowDeleteModal,
        videoId,
        setVideoId,
        setShowDeletedModal,
        setShowDeletedFailedModal,
    };
};

export default useTableDetails;
